import InputAdornment from '@mui/material/InputAdornment';
import BudgetIcon from '@mui/icons-material/AccountBalance';
import TextField from '@mui/material/TextField';
import theme from 'app/theme';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MerchantSpendRule } from 'API';
import { Controller, useFormContext } from 'react-hook-form';
import type { WheelEvent } from 'react';
interface MaximumTransactionAmountProps {
  selectedSpendRule?: MerchantSpendRule;
}

const MaximumTransactionAmountSpendRule = ({
  selectedSpendRule,
}: MaximumTransactionAmountProps): JSX.Element => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '16px',
        background: theme.palette.primary.lighter,
      }}
    >
      <Controller
        name="maximumTransactionAmount"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            type="number"
            error={!!errors.maximumTransactionAmount}
            helperText={
              typeof errors?.maximumTransactionAmount?.message === 'string'
                ? errors.maximumTransactionAmount.message
                : undefined
            }
            label={t('Maximum transaction amount')}
            placeholder={t('No maximum')}
            id="maximumTransactionAmount"
            variant="outlined"
            value={field.value ?? ''}
            fullWidth
            onChange={(e) => {
              const sanitized = e.target.value.replace(/[.,eE+\-\s]/g, '');
              field.onChange(sanitized);
            }}
            onKeyDown={(e) => {
              if (['.', ',', 'e', 'E', '+', '-', ' '].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              const paste = e.clipboardData.getData('text');
              const sanitized = paste.replace(/,/g, '').split(/[.]/)[0];
              if (sanitized && /^\d+$/.test(sanitized)) {
                field.onChange(sanitized);
              }
            }}
            InputProps={{
              inputProps: {
                min: 0,
                step: 1,
                autoComplete: 'off',
                autoCorrect: 'off',
                spellCheck: 'false',
                // SEE-1401: scrolling over a focused number input spins its value (an empty
                // field becomes "0"). Blur the native input on wheel so scrolling never edits
                // the limit. Must live in inputProps: TextField's own onWheel targets the
                // wrapper div, and blurring that does nothing.
                onWheel: (e: WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
              },
              startAdornment: (
                <InputAdornment position="start">
                  <BudgetIcon />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default MaximumTransactionAmountSpendRule;
